import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../../../api";
import { toast } from "react-toastify";
import useAuth from "../../Personal Attendance/useAuth";

/**
 * Round 6 — Digital Inspection attachments panel (per job).
 *
 * Lists, uploads, downloads, and soft-deletes documents for the current job
 * context. Backend: /api/DIAttachment/{list|upload|download|delete}.
 *
 *  - Any DI user can view + upload.
 *  - Delete shows only for the uploader OR a supervisor (RoleId 1/2/4/5);
 *    the SERVER re-enforces this regardless of what the UI shows.
 *  - 25 MB limit + extension allow-list are validated client-side for a fast
 *    message and AGAIN server-side as the real gate.
 *
 * Props: { jobNum, assemblySeq, partNum, lot, disabled }
 */
const ALLOWED_EXT = [".pdf", ".docx", ".xlsx", ".png", ".jpg", ".jpeg"];
const MAX_BYTES = 25 * 1024 * 1024;

const fmtSize = (n) => {
  const b = Number(n || 0);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const AttachmentsPanel = ({
  jobNum,
  assemblySeq = 0,
  partNum = null,
  lot = 0,
  disabled = false,
  registerOpenPicker,
}) => {
  const { empId, isItAdmin, isManager, isPresident } = useAuth();
  const fileRef = useRef(null);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const canDeleteRole = !!(isItAdmin || isManager || isPresident);

  const load = useCallback(async () => {
    if (!jobNum) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get("/DIAttachment/list", {
        params: { jobNum, assemblySeq, partNum: partNum || "", lot },
      });
      setItems(Array.isArray(res?.data) ? res.data : []);
    } catch {
      // A failed list shouldn't be noisy; show empty.
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [jobNum, assemblySeq, partNum, lot]);

  useEffect(() => {
    load();
  }, [load]);

  const onPick = useCallback(() => {
    if (disabled || uploading) return;
    fileRef.current?.click();
  }, [disabled, uploading]);

  // Round DI-3 — expose the picker to the page-level "Upload File" button.
  useEffect(() => {
    if (typeof registerOpenPicker === "function") registerOpenPicker(onPick);
  }, [registerOpenPicker, onPick]);

  const onFileChosen = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = ""; // allow re-pick of same file
      if (!file) return;

      const dot = file.name.lastIndexOf(".");
      const ext = dot >= 0 ? file.name.slice(dot).toLowerCase() : "";
      if (!ALLOWED_EXT.includes(ext)) {
        toast.error("Allowed types: PDF, DOCX, XLSX, PNG, JPG, JPEG.");
        return;
      }
      if (file.size > MAX_BYTES) {
        toast.error("File exceeds the 25 MB limit.");
        return;
      }

      const fd = new FormData();
      fd.append("file", file);
      fd.append("jobNum", jobNum);
      fd.append("assemblySeq", String(assemblySeq));
      fd.append("partNum", partNum || "");
      fd.append("lot", String(lot));

      setUploading(true);
      try {
        await api.post("/DIAttachment/upload", fd);
        toast.success("Document uploaded.");
        await load();
      } catch (err) {
        const msg = err?.response?.data?.error || "Upload failed.";
        toast.error(msg);
      } finally {
        setUploading(false);
      }
    },
    [jobNum, assemblySeq, partNum, lot, load]
  );

  const onDownload = useCallback(async (att) => {
    try {
      const res = await api.get(`/DIAttachment/download/${att.id}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = att.originalFileName || `attachment-${att.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed.");
    }
  }, []);

  const onDelete = useCallback(
    async (att) => {
      if (!window.confirm(`Delete "${att.originalFileName}"?`)) return;
      try {
        const res = await api.post(`/DIAttachment/delete/${att.id}`);
        if (res?.data === true) {
          toast.success("Document deleted.");
          await load();
        } else {
          toast.error("Delete failed.");
        }
      } catch (err) {
        const msg = err?.response?.data?.error || "Delete failed.";
        toast.error(msg);
      }
    },
    [load]
  );

  // Show the delete button only when the UI can tell the user is allowed
  // (uploader or supervisor). The server re-checks regardless.
  const mayDelete = (att) =>
    canDeleteRole ||
    String(att.uploadedByEmpId || "").trim() === String(empId || "").trim();

  if (!jobNum) return null;

  return (
    <div className="di-panel" style={{ marginTop: 12 }}>
      <div
        className="di-panel__head"
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        <span>
          <i className="ri-attachment-2 me-1" />
          Documents{items.length ? ` (${items.length})` : ""}
        </span>
        <button
          type="button"
          className="di-btn"
          style={{ border: "1px solid #d0d5dd" }}
          onClick={onPick}
          disabled={disabled || uploading}
          title="Upload a document for this job (PDF, DOCX, XLSX, PNG, JPG, JPEG; max 25 MB)"
        >
          <i className="ri-upload-2-line" />
          {uploading ? "Uploading..." : "Upload"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg"
          style={{ display: "none" }}
          onChange={onFileChosen}
        />
      </div>

      <div className="di-panel__body">
        {loading ? (
          <div className="text-muted small">Loading documents…</div>
        ) : items.length === 0 ? (
          <div className="text-muted small">
            No documents for this job yet. Use <b>Upload</b> to add one.
          </div>
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {items.map((att) => (
              <li
                key={att.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 4px",
                  borderBottom: "1px solid #f1f3f5",
                }}
              >
                <i className="ri-file-3-line" style={{ color: "#6c757d" }} />
                <button
                  type="button"
                  onClick={() => onDownload(att)}
                  title="Download"
                  style={{
                    border: 0,
                    background: "transparent",
                    color: "#0d6efd",
                    cursor: "pointer",
                    textAlign: "left",
                    padding: 0,
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {att.originalFileName}
                </button>
                <span className="text-muted small" style={{ whiteSpace: "nowrap" }}>
                  {fmtSize(att.fileSize)}
                </span>
                {mayDelete(att) ? (
                  <button
                    type="button"
                    onClick={() => onDelete(att)}
                    title="Delete"
                    style={{
                      border: 0,
                      background: "transparent",
                      color: "#dc3545",
                      cursor: "pointer",
                      padding: "0 4px",
                    }}
                  >
                    <i className="ri-delete-bin-6-line" />
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default React.memo(AttachmentsPanel);
